/**
 * G2eGente - Card de Link de Divulgação do App para Funcionários
 * Usado em CompanySettings
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Loader2, 
  Copy, 
  RefreshCw, 
  Download,
  QrCode,
  Smartphone,
  Link as LinkIcon,
  CheckCircle,
  Share2
} from 'lucide-react';
import { companiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

export default function EmployeeAppAccessCard() {
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [accessData, setAccessData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAccessCode();
  }, []);

  const fetchAccessCode = async () => {
    try {
      setLoading(true);
      const res = await companiesAPI.getAccessCode();
      setAccessData(res.data);
    } catch (error) {
      console.error('Erro ao buscar código de acesso:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Tem certeza? O código anterior será invalidado e funcionários precisarão do novo link.')) {
      return;
    }
    
    try {
      setRegenerating(true);
      const res = await companiesAPI.regenerateAccessCode();
      setAccessData(prev => ({
        ...prev,
        access_code: res.data.access_code
      }));
      toast.success('Código regenerado com sucesso');
    } catch (error) {
      toast.error('Erro ao regenerar código');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyLink = () => {
    const fullUrl = `${window.location.origin}/app/e/${accessData.access_code}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('employee-qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 400, 400);
      
      const link = document.createElement('a');
      link.download = `qrcode-acesso-funcionarios-${accessData.access_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    toast.success('QR Code baixado!');
  };

  const handleShare = async () => {
    const fullUrl = `${window.location.origin}/app/e/${accessData.access_code}`;
    const shareData = {
      title: 'G2eGente - Acesso do Funcionário',
      text: `Acesse o app da ${accessData.company_name} usando este link:`,
      url: fullUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
        </CardContent>
      </Card>
    );
  }

  const fullUrl = `${window.location.origin}/app/e/${accessData?.access_code}`;

  return (
    <Card className="border-cyan-200">
      <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-600">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-cyan-900">Divulgação do App para Funcionários</CardTitle>
              <CardDescription className="text-cyan-700">
                Compartilhe o link ou QR Code para seus funcionários acessarem o app
              </CardDescription>
            </div>
          </div>
          <Badge className="bg-cyan-600 text-white">
            {accessData?.access_code}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-6 space-y-6">
        {/* Instruções */}
        <Alert className="bg-amber-50 border-amber-200">
          <AlertDescription className="text-amber-800 text-sm">
            <strong>Como funciona:</strong>
            <ol className="list-decimal list-inside mt-2 space-y-1">
              <li>O funcionário acessa o link ou escaneia o QR Code</li>
              <li>Informa seu CPF e senha (padrão: 4 primeiros dígitos do CPF)</li>
              <li>No primeiro acesso, pode criar uma senha personalizada</li>
            </ol>
          </AlertDescription>
        </Alert>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Link de Acesso */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Link de Acesso</label>
              <div className="flex gap-2">
                <Input 
                  value={fullUrl}
                  readOnly
                  className="bg-slate-50 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="shrink-0"
                  title="Copiar link"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleShare} className="bg-cyan-600 hover:bg-cyan-700">
                <Share2 className="w-4 h-4 mr-2" />
                Compartilhar
              </Button>
              <Button variant="outline" onClick={handleCopyLink}>
                <LinkIcon className="w-4 h-4 mr-2" />
                Copiar Link
              </Button>
            </div>

            <div className="pt-4 border-t">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRegenerate}
                disabled={regenerating}
                className="text-slate-500 hover:text-slate-700"
              >
                {regenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Gerar Novo Código
              </Button>
              <p className="text-xs text-slate-500 mt-1">
                O código anterior será invalidado
              </p>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center">
            <div className="p-4 bg-white rounded-xl shadow-sm border">
              <QRCodeSVG
                id="employee-qr-code"
                value={fullUrl}
                size={180}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#0e7490"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3"
              onClick={handleDownloadQR}
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar QR Code
            </Button>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Imprima e coloque em murais,<br />crachás ou comunicados
            </p>
          </div>
        </div>

        {/* Info adicional */}
        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
          <div className="flex items-start gap-2">
            <QrCode className="w-4 h-4 mt-0.5 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">Dica de uso:</p>
              <p>Inclua o QR Code nos crachás dos funcionários, murais de aviso ou envie o link por WhatsApp/email interno.</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
